import { useEffect, useMemo, useRef, useState } from 'react';
import { registerCurveImageProvider } from '../generateReport';
import { collectReportCurveImages } from '../fanCurveImages';
import { generateStateKey, readGeneratedReport, subscribeGeneratedReports } from '../reportTabData';
import { drawFanDataCurves, type FanDataRow } from '../fanCurves';
import type { GeneratedTabPaneProps } from './GeneratedReportTabs';

/**
 * Phase 5d — the "Fan Data" tab (tabs/fan_data/view/fan_data.js ~652 lines +
 * tmpl/fan_data.tmpl + tmpl/report.ahu.tmpl). What the legacy tab REALLY does:
 *
 *  - 'generate:report:fan_data' single-AHU (fan_data.js:227-267): POST
 *    fan_selection {…state, fan_data:true} — the RESPONSE is the fan-row JSON
 *    (FanSelectionView, report/views.py:922-932) — then, in the success
 *    callback, render() the shell, renderReport() the rows, and getCurve()
 *    fires the SECOND fan_selection POST (whole state minus fan_data +
 *    img[]) that actually creates the celery PDF task. Multi-AHU (:175-225):
 *    render() runs synchronously, then a background chain fetches each AHU,
 *    re-renders the tab per AHU, and sends ONE batched {ahu_ids_json, img[],
 *    date} POST. Both flows live in the kit's fan_data dispatch + the
 *    fanCurveImages provider; this pane renders whatever landed in the
 *    reportTabData cache (successive multi-AHU writes reproduce the
 *    render-replaces-render sequence — the pane ends on the LAST fetched
 *    AHU's fans, like the legacy DOM).
 *
 *  - renderReport (fan_data.js:41-59) fills `.curve-secction` with
 *    report.ahu.tmpl: a "No Fan section in Air Handling Unit" note when the
 *    response is empty, else one .report-per-page div per fan — carrying the
 *    legacy `hidden` class, so the tab's VISIBLE content is just the shell —
 *    each holding the `curves_<id>` canvas that drawCurves (:341-383) paints
 *    for guard-passing fans (curve && curve.sp && curve.system_curve) and
 *    getCurve/the multi chain rasterize into img[]. `{{prefix}}` and
 *    `{{choosen}}` are not serializer fields: ids render bare and the
 *    ' no-curves ' class is ALWAYS present (report.ahu.tmpl:8-13).
 *    The canvases here are the DOM-parity copies; the kit captures equal
 *    OFFSCREEN canvases drawn by the same engine (fanCurves.ts), because the
 *    dispatch cannot await a React commit the way the legacy captured the
 *    canvases it had just synchronously inserted.
 *
 *  Dead legacy code, deliberately NOT ported:
 *  - export_pdf/viewPDF (:87-143): only caller is the fully-commented socket
 *    block (:252-266) — dead, so #fan_structure stays empty forever.
 *  - openGeneratedReport (:145-153): the listener is live but reads
 *    `this.data[state]`, which only the commented block ever writes →
 *    generated stays false and re-opening shows the empty board (fan_data is
 *    NOT in REOPENABLE_TABS; this pane only renders in generate mode).
 *  - export_csv (:281-339): trigger chain starts at the header's
 *    'click #export_csv' (main_header.js:62) and main_header.tmpl renders no
 *    such button — unreachable (and it, too, reads the never-written cache).
 *  - events 'click .nav-tabs li' → activateLabel (:20): the method does not
 *    exist on the view (and the template has no .nav-tabs) — Backbone
 *    delegateEvents silently skips it.
 *  - calcBHPTotal (:155-167) and drawCurveLabel (:602-643): never called.
 */

// The 5b seam (generateReport.ts): registering at module scope guarantees the
// provider exists before ReportsBoardPage can dispatch — this module loads
// with GENERATED_TAB_PANES, which the page imports alongside the kit.
registerCurveImageProvider(collectReportCurveImages);

interface FanDataResult {
  models: FanDataRow[];
  /** Bumped per cache write so multi-AHU re-renders mount FRESH canvases —
   *  the legacy html() insert always starts from blank bitmaps. */
  seq: number;
}

function readModels(key: string | null): FanDataRow[] | null {
  if (!key) return null;
  const entry = readGeneratedReport(key);
  return entry && Array.isArray(entry.models) ? (entry.models as FanDataRow[]) : null;
}

const FanDataTab = ({ report, faded, rendered, states }: GeneratedTabPaneProps) => {
  // The dispatch state's cache key (ONE state both single and batched multi —
  // fan_data is in BATCHED_MULTI_AHU_REPORTS, so there is no fan-out).
  const key = useMemo(() => {
    const last = states && states.length ? states[states.length - 1] : null;
    return last ? generateStateKey(last) : null;
  }, [states]);

  const [result, setResult] = useState<FanDataResult | null>(() => {
    const models = readModels(key);
    return models ? { models, seq: 0 } : null;
  });

  useEffect(() => {
    const models = readModels(key);
    setResult(models ? { models, seq: 0 } : null);
    return subscribeGeneratedReports(() => {
      const next = readModels(key);
      setResult((prev) =>
        next ? { models: next, seq: (prev?.seq ?? 0) + 1 } : prev
      );
    });
  }, [key]);

  // drawCurves (fan_data.js:341-383) over the freshly-inserted canvases. The
  // legacy loop is unguarded — a draw throw aborts the REMAINING models (the
  // exception escapes to the console); React must contain it, so one
  // try/catch wraps the whole pass.
  const curveHostRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const host = curveHostRef.current;
    if (!host || !result) return;
    try {
      drawFanDataCurves(
        result.models.map((m) => ({
          id: m.id,
          canvas: host.querySelector<HTMLCanvasElement>(`.curves_${m.id}`) as HTMLCanvasElement,
        })).filter((e) => e.canvas),
        result.models
      );
    } catch {
      // legacy: uncaught in the success callback / warn-and-continue (multi).
    }
  }, [result]);

  return (
    <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
      {/* The pane is empty until render() runs — single: the POST success
          (:243); multi: synchronously at dispatch (:177, the kit resolves the
          batched dispatch immediately). */}
      {rendered && (
        <>
          {/* fan_data.tmpl:1-5 — tooltip WITHOUT the leading space. */}
          <div className="container-header clearfix">
            <h3 className="left">
              <span className="group-name" tooltip={`: ${report.name}`}>
                Engineering
              </span>
              <span className="report-name">{report.name}</span>
            </h3>
          </div>
          <div className="report-content col-xs-12">
            <div className="content">
              <div className="content-item full-height" style={{ overflow: 'auto' }}>
                <div
                  className={`preloader-container generation-in-progress${
                    faded ? ' opacity-0' : ''
                  }`}
                >
                  <div className="preloader active">Report generation in progress...</div>
                </div>
                {/* renderReport target (fan_data.js:55) — report.ahu.tmpl. */}
                <div className="curve-secction" ref={curveHostRef}>
                  {result && !result.models.length && (
                    <span className="no-data-note">
                      <span>No Fan section in Air Handling Unit</span>
                    </span>
                  )}
                  {result &&
                    result.models.map((m) => (
                      <div
                        key={`${result.seq}-${m.id}`}
                        className={`report-per-page per-fan fan_${m.id} hidden`}
                        style={{ height: '100%' }}
                      >
                        <div className="row output-part" style={{ height: '42%' }}>
                          <div
                            className="col-xs-11 output-part-data"
                            style={{
                              height: '100%',
                              border: '1px solid #eee',
                              paddingTop: 18,
                              backgroundColor: '#fff',
                            }}
                          >
                            <div
                              className="col-xs-12 tab-pane fade active in"
                              style={{ height: '100%' }}
                              id={`${m.id}Properties_Performance_Output`}
                              // the legacy div carries a (non-standard) name
                              // attribute (tmpl:9) — React passes it through.
                              name={`${m.id}Properties_Performance_Output`}
                            >
                              <div
                                className="col-xs-12 col-lg-4 right"
                                style={{ paddingBottom: 18 }}
                              >
                                <div className="fan-curve">
                                  {/* {{#unless choosen}} — choosen is never a
                                      serializer field → always ' no-curves '. */}
                                  <div className="fan-curve-center  no-curves ">
                                    <canvas
                                      id={`curves_${m.id}`}
                                      className={`curves curves_${m.id}`}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
                <div id="fan_structure" className="row full-height" />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FanDataTab;
